import React, { useState, useRef, useEffect, useContext } from 'react';
import { useSparks } from '../../contexts/SparksContext';
import SparksAddyBurst from '../../components/Sparks/SparksAddyBurst';
import { useHideAskAddy } from '../../contexts/NavVisibilityContext';
import AddyAvatar from '../../components/Sparks/AddyAvatar';
import useCosmeticCatalogue from '../../components/Sparks/useCosmeticCatalogue';
import useBeginLessonTally from '../../components/Sparks/useBeginLessonTally';
import SparksStarsUpdate from '../../components/Sparks/SparksStarsUpdate';
import { get, post, put } from '@/utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import SingleQuestionPage from '@/components/Quiz/SingleQuestionPage/SingleQuestionPage';
import { AuthContext } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/shadcn/button';

import correctSound from '@/media/correct-answer-sound.mp3';
import correctAudio from '@/media/correct-audio.mp3';
import incorrectAudio from '@/media/incorrect-audio.mp3';

import ProgressBar from '@/components/ProgressBar/ProgressBar';  // Import the ProgressBar component
import '@/components/HomePageV2.css';
import { useIsPhone } from '@/hooks/useMediaQuery';
import { exitVideoFullscreen } from '@/utils/videoFullscreen';
import ErrorBoundary from '@/components/ErrorBoundary';
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import the styles

function LearnCoreSkill({ onQuizStart, preventNavigation, onStateChange, onInputChange }) {
    const { lessonId } = useParams();
    const [latestLesson, setLatestLesson] = useState(null);
    const [showAnimation, setShowAnimation] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionStarted, setSessionStarted] = useState(false);
    const [isTalking, setIsTalking] = useState(false);
    const [currentLessonStep, setCurrentLessonStep] = useState(null);
    const [basiclessonText, setBasicLessonText] = useState('');
    // null means no video is playing. Previously false, which caused `false === 0` on step 0
    // to always evaluate as false, preventing the first video from ever playing.
    const [whichVideo, setWhichVideo] = useState(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [selectAnswerMessage, setSelectAnswerMessage] = useState(true);
    const [answerSelected, setAnswerSelected] = useState(false);
    const [explanationText, setExplanationText] = useState('');
    const [showButtons, setShowButtons] = useState(true);
    const [isInitialStep, setIsInitialStep] = useState(true);
    const [showCelebration, setShowCelebration] = useState(false);

    const [addyisQuestioning, setAddyisQuestioning] = useState(false);

    const audioElementRef = useRef(null);
    const isPhone = useIsPhone();
    const lessonPlayerRef = useRef(null);
    const audioContextRef = useRef(null);
    const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const { user, logout, loggedInChild } = useAuth();
    const sparks = useSparks();
    const addyContainerRef = useRef(null); // Sparks bubbles spawn from Addy
    const cosmeticCatalogue = useCosmeticCatalogue(loggedInChild?._id);
    useBeginLessonTally(loggedInChild?._id, sessionStarted); // for the end-of-lesson recap
    const navigate = useNavigate();

    const [childTopic, setChildTopic] = useState(null);
    const [topicId, setTopicId] = useState(null);
    const [isLessonComplete, setIsLessonComplete] = useState(false);
    const [showCharacter, setShowCharacter] = useState(false);
    useHideAskAddy(showCharacter);

    const [isCurrentStepQuestion, setIsCurrentStepQuestion] = useState(false);
    const [currentQuizQuestion, setCurrentQuizQuestion] = useState(null);

    const urlParam = new URLSearchParams(window.location.search);
    const isCoreSkill = urlParam.get('isCoreSkill') === 'true';
    const coreSkillTopicId = urlParam.get('coreSkillTopicId');
    const dashboardSessionId = urlParam.get('dashboardSessionId');
    const coreSkillSource = urlParam.get('source');
    const isDashboardCoreSkill = coreSkillSource === 'dashboard_progress' || !!dashboardSessionId;

    // Add this function near the top of your component
    const getDisplayType = (type) => {
        if (['Question', 'Multiple-Choice-Question', 'Free-Text-Question', 'Blank-Box-Question', 'Drag-And-Drop-Question', 'Mixed'].includes(type)) {
            return 'Question';
        }
        return type;
    };

    useEffect(() => {
        fetchLessonAndChildTopic();
    }, [lessonId, coreSkillTopicId, loggedInChild?._id]);


    // Add effect to notify parent of state changes
    // remove the params and dependencies that i don't need at some point
    useEffect(() => {
        if (onStateChange) {
            onStateChange({
                latestLesson,
                currentLessonStep,
                totalSteps: latestLesson?.steps?.length,
                isQuizMode: false,
                lessonTitle: latestLesson?.title,
                currentView: 'lesson',
                sessionStarted,
                basiclessonText,
                isLessonComplete
            });
        }
    }, [
        currentLessonStep,
        latestLesson,
        sessionStarted,
        basiclessonText,
        isLessonComplete,
        onStateChange
    ]);


    const fetchLessonAndChildTopic = async () => {
        try {
            setLoading(true);
            if (loggedInChild) {
                // First, fetch the child's current topic
                let childTopic;
                if (coreSkillTopicId) {
                    const isDashboard = coreSkillSource === 'dashboard_progress' || !!dashboardSessionId;
                    const endpoint = isDashboard
                        ? `/childTopics/dashboard-core-skill/${loggedInChild._id}/${coreSkillTopicId}`
                        : `/childTopics/core-skill/${loggedInChild._id}/${coreSkillTopicId}`;

                    childTopic = await get(endpoint, true);

                    if (!childTopic) {
                        throw new Error("No core skills topic found for this child");
                    }

                    const lessonField = isDashboard ? 'currentDashboardCoreSkillLesson' : 'currentCoreSkillLesson';
                    const stepField = isDashboard ? 'currentDashboardCoreSkillLessonStep' : 'currentCoreSkillLessonStep';

                    // redirect to quiz if user has completed the lesson
                    if ((childTopic[stepField] || 0) + 1 >= (childTopic[lessonField]?.steps.length || 0)) {
                        setIsLessonComplete(true);
                    }

                    setChildTopic(childTopic);
                    setTopicId(coreSkillTopicId);
                    setLatestLesson(childTopic[lessonField]);
                    setCurrentLessonStep(childTopic[stepField] || 0);
                } else {
                    throw new Error("No current core skill topic found for this child");
                }
            } else {
                throw new Error("No logged in child found");
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching lesson and child topic:', err);
            setError(`Failed to fetch the lesson: ${err.message}`);
            setLoading(false);
        }
    };





    const updateAudioAndAnimation = (step, stepIndex) => {
        //console.log('Updating audio and animation for step:', stepIndex);
        setWhichVideo(stepIndex);
        setIsCurrentStepQuestion(getDisplayType(step.Type) === 'Question');

        if (getDisplayType(step.Type) !== 'Question') {
            // For non-question steps, set answerSelected to true to allow animation to play
            setAnswerSelected(true);
            setAddyisQuestioning(false);
        } else {
            // Reset for question steps
            setAnswerSelected(false);
            setAddyisQuestioning(true);
        }

    };

    //--------------------------------------------------------------------------------
    //This useEffect sets the currentQuizQuestion in the case that the current step is a question
    //and also plays the audio for the current step
    //--------------------------------------------------------------------------------
    useEffect(() => {
        if (sessionStarted && latestLesson && latestLesson.steps[currentLessonStep]) {
            const currentStep = latestLesson.steps[currentLessonStep];
            //remember : currentLessonStep is just a number that is the index 
            // of the current step in the lesson
            //currentStep is the step object for the current step in the lesson

            updateAudioAndAnimation(currentStep, currentLessonStep);

            // Update the current quiz question if it's a question step
            if (getDisplayType(currentStep.Type) === 'Question') {
                setCurrentQuizQuestion(currentStep.quizQuestionId);
            } else {
                setCurrentQuizQuestion(null);
            }

            // Small delay to ensure audio is loaded
            if (!isInitialStep) {
                setTimeout(() => {
                    playAudio(currentStep.audio);
                }, 100);
            }

        }
    }, [currentLessonStep, sessionStarted, latestLesson]);

    const playAudio = (audioSource) => {
        if (audioElementRef.current) {
            initializeAudioContext();
            if (audioSource) {
                audioElementRef.current.src = audioSource;
                audioElementRef.current.load();
            }
            const playPromise = audioElementRef.current.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        //console.log('Audio started playing');
                        setIsTalking(true);
                        setShowButtons(false); // Hide buttons while audio is playing
                    })
                    .catch(e => {
                        console.error("Error playing audio:", e);
                        setNeedsUserInteraction(true);
                    });
            }

            // Add an 'ended' event listener to show buttons when audio finishes
            audioElementRef.current.onended = () => {
                //console.log('Audio playback ended');
                setIsTalking(false);
                setShowButtons(true); // Show buttons after audio finishes
            };
        }
    };

    const handleStartLearning = async () => {
        if (latestLesson) {
            try {
                setBasicLessonText(latestLesson.steps[currentLessonStep]?.TalkingPoint);
                setCurrentLessonStep(currentLessonStep);
                setSessionStarted(true);
                setShowCharacter(true);

                // Play the audio for the first step
                if (latestLesson.steps[currentLessonStep].audio) {
                    playAudio(latestLesson.steps[currentLessonStep].audio);
                    setHasUserInteracted(true);
                }

                setIsInitialStep(false);
                setAnswerSelected(false);
                setAddyisQuestioning(false);
                setSelectAnswerMessage(true);
                setShowButtons(true);
                setExplanationText('');
                setShowAnimation(true);
            } catch (err) {
                console.error('Error starting lesson:', err);
            }
        }
    };

    const handleContinueLearning = async () => {
        const nextStep = currentLessonStep + 1;
        if (latestLesson && nextStep < latestLesson.steps.length) {
            setBasicLessonText(latestLesson.steps[nextStep].TalkingPoint);
            setCurrentLessonStep(nextStep);
            setAnswerSelected(false);
            setAddyisQuestioning(false);
            setSelectAnswerMessage(true);
            setExplanationText('');
            setShowButtons(true);

            // Update the current lesson step in the database
            if (loggedInChild && childTopic) {
                try {
                    const stepEndpoint = isDashboardCoreSkill
                        ? `/childTopics/dashboard-core-skill/${loggedInChild._id}/${topicId}/lessonStep`
                        : `/childTopics/core-skill/${loggedInChild._id}/${topicId}/lessonStep`;
                    await put(stepEndpoint, { currentLessonStep: nextStep, isCoreSkill }, true);
                } catch (error) {
                    console.error('Failed to update lesson step in database:', error);
                }
            }

            // The audio will be played by the useEffect hook
        } else {
            setIsLessonComplete(true);
        }
    };

    const handleGoBack = async () => {
        const previousStep = currentLessonStep - 1;
        if (previousStep >= 0) {
            setBasicLessonText(latestLesson.steps[previousStep].TalkingPoint);
            setCurrentLessonStep(previousStep);
            setAnswerSelected(false);
            setAddyisQuestioning(false);
            setSelectAnswerMessage(true);
            setExplanationText('');
            setShowButtons(true);

            // Update the current lesson step in the database
            if (loggedInChild && childTopic) {
                try {
                    const stepEndpoint = isDashboardCoreSkill
                        ? `/childTopics/dashboard-core-skill/${loggedInChild._id}/${topicId}/lessonStep`
                        : `/childTopics/core-skill/${loggedInChild._id}/${topicId}/lessonStep`;
                    await put(stepEndpoint, { currentLessonStep: previousStep, isCoreSkill }, true);
                } catch (error) {
                    console.error('Failed to update lesson step in database:', error);
                }
            }
        } else {
            //console.log("Already at the first step of the lesson");
        }
        setIsLessonComplete(false);
    };


    const handleAnswerSelect = (isCorrect, selectedAnswer) => {
        //console.log('Answer selected:', { isCorrect, selectedAnswer });

        setAddyisQuestioning(false);


        if (latestLesson && latestLesson.steps[currentLessonStep]) {
            const step = latestLesson.steps[currentLessonStep];
            // Sparks (once per step per day): recorded server-side, drawn from Addy on reply.
            if (loggedInChild?._id && latestLesson?._id) {
                put(`/childTopics/${loggedInChild._id}/step-answer`, { lessonId: latestLesson._id, stepIndex: currentLessonStep, correct: !!isCorrect }, true)
                    .then((res) => sparks.pushFromResponse(res))
                    .catch(() => {}); // non-fatal
            }
            setSelectAnswerMessage(false);
            setShowButtons(false);

            const playFeedbackAudio = () => {
                // Play correct or incorrect audio
                setShowCelebration(false);
                const audioToPlay = isCorrect ? correctAudio : incorrectAudio;
                playAudio(audioToPlay);

                if (audioElementRef.current) {
                    audioElementRef.current.onended = () => {
                        setCurrentQuizQuestion(null);
                        setAnswerSelected(true);
                        setShowButtons(true);
                        if (step.AnswerExplanation) {
                            setExplanationText(step.AnswerExplanation);

                            // Handle the delay between animation and explanation audio start
                            if (step.animationDelay >= 0) {
                                playAudio(step.AnswerExplanationAudio);
                                setTimeout(() => {
                                    setWhichVideo(currentLessonStep);
                                }, step.animationDelay * 1000);
                            } else {
                                setWhichVideo(currentLessonStep);
                                setTimeout(() => {
                                    playAudio(step.AnswerExplanationAudio);
                                }, Math.abs(step.animationDelay) * 1000);
                            }
                        } else {
                            // If there's no explanation, don't show animation or play audio
                            setWhichVideo(null);
                            setExplanationText('');
                        }
                    };
                }
            };

            if (isCorrect) {
                // First play correctSound if the answer is correct
                playAudio(correctSound);
                setShowCelebration(true);

                // Set up the onended event for correctSound to play the feedback audio
                if (audioElementRef.current) {
                    audioElementRef.current.onended = playFeedbackAudio;
                }
            } else {
                // If the answer is incorrect, directly play the feedback audio
                playFeedbackAudio();
            }
        }
    };

    const initializeAudioContext = () => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
    };


    // need this function to make audio autoplay work on ios
    // ios requires user interaction before autoplaying
    const handlePlayAudio = () => {
        if (audioElementRef.current) {
            audioElementRef.current.play()
                .then(() => setNeedsUserInteraction(false))
                .catch(error => console.error('Error playing audio:', error));
        }
        setHasUserInteracted(true);
    };




    const handleStartQuiz = async () => {
        if (!latestLesson || !loggedInChild || !topicId) {
            console.error('Missing required data to start quiz');
            return;
        }

        if (preventNavigation) {

            onQuizStart(latestLesson, childTopic, user, loggedInChild);
        }
        else {

            try {
                // Create quiz submission with 20 random questions
                const response = await post('/quiz/quiz-submissions', {
                    lessonId: latestLesson._id,
                    userId: user._id,
                    childId: loggedInChild._id,
                    topicId,
                    QuizStatus: 'Pending',
                    isCoreSkill: true,
                    track: 'core_skill',
                    isForcedRed: false,
                }, true);

                const quizSubmissionId = response._id;

                // Navigate to the quiz page
                navigate(`/core-skills/quiz/${topicId}/${latestLesson._id}`, {
                    state: {
                        quizSubmissionId,
                        dashboardSessionId,
                        coreSkillSource,
                    },
                });
            } catch (error) {
                console.error('Error starting quiz:', error);
                // Handle error (e.g., show error message to user)
            }
        }

    };


    if (loading) {
        return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }

    if (error) {
        return <div className="flex items-center justify-center min-h-screen text-red-500">{error}</div>;
    }

    // console.log({latestLesson, currentLessonStep});
    const currentStep = latestLesson && latestLesson.steps && latestLesson?.steps[currentLessonStep];

    return (
        <ErrorBoundary>
            <div className="min-h-screen p-4 bg-slate-200">
                {/* Hidden audio element */}
                <audio ref={audioElementRef} style={{ display: 'none' }} />

                <div className="w-full max-w-none" style={{ opacity: 1.0 }}>
                    {!sessionStarted ? (
                        // Initial view with centered content
                        <div className="flex flex-col items-center justify-center min-h-screen">
                            <h1 className="text-3xl font-semibold text-foreground mb-8 text-center px-4">Welcome to Your Lesson!</h1>
                            <Button
                                onClick={handleStartLearning}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 text-xl transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                {currentLessonStep === 0 ? "Start Learning!" : "Continue Learning"}
                            </Button>
                        </div>
                    ) : (
                        // Lesson content view
                        <div className="flex flex-col">

                            {isLessonComplete ? (
                                <div className="flex flex-col items-center justify-center px-4">
                                    <p className="text-xl sm:text-2xl font-bold mb-4 text-center">
                                        You've finished the lesson! Are you ready to do some practice questions?
                                    </p>
                                    <SparksStarsUpdate />
                                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                                        <Button
                                            onClick={handleStartQuiz}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            Start Practice Questions
                                        </Button>
                                        <Button
                                            onClick={handleGoBack}
                                            className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                                        >
                                            Go Back
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                // Existing lesson content
                                <div className="flex flex-col">
                                    {/* Container for SingleQuestionPage, ReactPlayer, and ProgressBar */}
                                    <div className={`relative w-full sm:w-[90%] md:w-[90%] lg:w-[80%] bg-white mx-auto mb-2 border-2 border-black rounded-lg shadow-lg shadow-2xl ${isCurrentStepQuestion ? 'max-md:!h-[calc(100dvh_-_var(--nav-height,64px)_-_95px)]' : 'max-md:!h-auto max-md:aspect-video'}`} style={{ height: '75vh' /*`${isCurrentStepQuestion ? '60vh' : '50vh'}`*/ }}>
                                        {latestLesson && currentLessonStep >= 0 && (
                                            <div className="absolute inset-0 z-0" style={{ overflow: 'hidden' }}>
                                                {latestLesson.steps[currentLessonStep].animation && (
                                                    <ReactPlayer
                                                        url={latestLesson.steps[currentLessonStep].animation}
                                                        playing={whichVideo === currentLessonStep && (!isCurrentStepQuestion || answerSelected)}
                                                        playbackRate={1 / (latestLesson.steps[currentLessonStep].slowDownMultiplier || 1)}
                                                        muted={true}
                                                        controls={true}
                                                        config={{
                                                            file: {
                                                                attributes: {
                                                                    controlsList: "nofullscreen nodownload noplaybackrate",
                                                                },
                                                            },
                                                        }}
                                                        width="100%"
                                                        height="100%"
                                                        ref={lessonPlayerRef}
                                                        playsinline={true}
                                                        onError={(e) => console.error('Error playing video:', e)}
                                                        onReady={() => console.log('Video is ready to play')}
                                                        onStart={() => console.log('Video started playing')}
                                                        onPause={() => console.log('Video paused')}
                                                        onEnded={() => {
                                                          console.log('Video ended');
                                                          // Phones: if the child chose fullscreen, drop back to the lesson so Continue is visible.
                                                          if (isPhone) exitVideoFullscreen(lessonPlayerRef.current?.getInternalPlayer());
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {isCurrentStepQuestion && (!answerSelected || !currentStep.AnswerExplanation) && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="bg-white rounded-lg shadow-lg p-4 w-full h-full overflow-y-auto flex items-center justify-center max-md:items-start max-md:p-2">
                                                    {currentQuizQuestion && (
                                                        <SingleQuestionPage
                                                            key={`question-${currentLessonStep}`}
                                                            quizQuestion={currentQuizQuestion}
                                                            onAnswerSelect={handleAnswerSelect}
                                                            ButtonsAreClickable={!answerSelected}
                                                            onInputChange={onInputChange}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Modified ProgressBar and Character container */}
                                    <div className="w-full sm:w-[90%] md:w-[80%] lg:w-[70%] mx-auto mt-4 relative">
                                        <div className="flex flex-col items-center justify-center">
                                            {latestLesson && (
                                                <div className="w-full max-w-[300px]"> {/* Constrain width here */}
                                                    <ProgressBar
                                                        currentQuestion={currentLessonStep + 1}
                                                        totalQuestions={latestLesson.steps.length}
                                                        labelText="Step"
                                                    />
                                                </div>
                                            )}

                                            {/* Character container */}
                                            {isCurrentStepQuestion && <SparksAddyBurst addyRef={addyContainerRef} showCounter={isCurrentStepQuestion && !answerSelected} />}
                                            {showCharacter && (
                                                <div ref={addyContainerRef} className="character-container absolute right-0 bottom-0">
                                                    {/* Celebrates a correct answer with the child's chosen clip; otherwise talks while audio plays, asks on question steps. */}
                                                    <div className="character-container__addy">
                                                        <AddyAvatar
                                                            equipped={sparks.equipped}
                                                            catalogue={cosmeticCatalogue}
                                                            size="100%"
                                                            bleed
                                                            mood={!isTalking ? 'sway' : isCurrentStepQuestion && addyisQuestioning ? 'asking' : 'talking'}
                                                            celebrating={showCelebration}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* User Interaction Buttons */}
                                    <div className="mt-2 flex flex-wrap justify-center items-center gap-2">
                                        {isCurrentStepQuestion && selectAnswerMessage ? (
                                            <p>Please select an answer to continue.</p>
                                        ) : (
                                            <>
                                                {currentLessonStep > 0 && showButtons && (
                                                    <Button
                                                        onClick={handleGoBack}
                                                        className="bg-gray-500 text-white px-3 py-1 sm:px-4 sm:py-2 text-sm sm:text-base mr-2"
                                                    >
                                                        Go Back
                                                    </Button>
                                                )}
                                                {showButtons && (
                                                    <Button
                                                        onClick={handleContinueLearning}
                                                        className="bg-blue-500 text-white px-3 py-1 sm:px-4 sm:py-2 text-sm sm:text-base"
                                                    >
                                                        Continue learning
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Lesson Text Display */}
                                    {(basiclessonText || explanationText) && (
                                        <div className="w-full sm:w-[90%] md:w-[80%] lg:w-[70%] mx-auto p-4 bg-slate-300 shadow-lg border-2 border-black rounded-lg mt-4 mb-4">
                                            <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">Addy's Response:</h3>
                                            <p className="text-base sm:text-lg text-gray-700 whitespace-pre-wrap text-center">
                                                {explanationText || basiclessonText}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Add this near your other UI elements */}
                {needsUserInteraction && !hasUserInteracted && (
                    <Button onClick={handlePlayAudio} className="bg-blue-500 text-white px-4 py-2 rounded">
                        Play Audio
                    </Button>
                )}
            </div>
        </ErrorBoundary>
    );
}

export default LearnCoreSkill;
